import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router';
import { type AuthResponse, getFieldErrorMessage } from '~/vendor/cx-web/feature-auth';
import { Button } from '~/vendor/cxy/components/Button';
import { Input } from '~/vendor/cxy/components/Input';

import styles from './styles/ForgotPasswordForm.module.css';

export type ForgotPasswordFormProps = {
  authBasePath?: string;
  // Seeds the email field when the modal was opened with a known email (e.g. a password-reset deep
  // link's `oc_prefill_email` query param). Mirrors SignupForm's `prefillEmail`.
  prefillEmail?: string;
  repubPath: string;
};

export const ForgotPasswordForm = ({
  authBasePath = '/plus/auth',
  prefillEmail,
  repubPath,
}: ForgotPasswordFormProps) => {
  const fetcher = useFetcher<AuthResponse>();
  const [showErrorState, setShowErrorState] = useState(false);

  const { t } = useTranslation(['label']);

  const data = fetcher.data;
  const hasErrorMessage = data?.message && !data?.success;

  useEffect(() => {
    if (data?.fieldErrors || hasErrorMessage) {
      setShowErrorState(true);
    }
  }, [data?.fieldErrors, hasErrorMessage]);

  const onChange = () => {
    setShowErrorState(false);
  };

  return (
    <fetcher.Form
      action={`${repubPath}${authBasePath}/forgot-password`}
      className={styles.forgotPasswordForm}
      data-testid="forgot-password-form"
      method="post"
    >
      <p className={styles.helperText}>{t('CX_AUTH_FORGOT_PASSWORD_INSTRUCTIONS')}</p>
      <Input
        autoComplete="email"
        // `key` is keyed off prefillEmail so a change in the seeded value remounts the uncontrolled
        // input and its defaultValue takes effect (React only reads defaultValue on mount) — same
        // pattern as SignupForm's email field.
        defaultValue={prefillEmail}
        errors={
          showErrorState ? ([getFieldErrorMessage(data?.fieldErrors?.email)].filter(Boolean) as string[]) : undefined
        }
        id="email"
        key={`email-${prefillEmail ?? ''}`}
        name="email"
        onChange={onChange}
        placeholder={t('CX_AUTH_RESET_PLACEHOLDER_EMAIL')}
        type="email"
      />
      {data?.message && (
        <p className={`${styles.responseMessage}${hasErrorMessage ? ` ${styles.errorMessage}` : ''}`}>{data.message}</p>
      )}

      <div className={styles.buttonSection}>
        <Button isLoading={fetcher.state !== 'idle'} size="large" type="submit" variant="secondary">
          {t('CX_AUTH_RESET_PASSWORD')}
        </Button>
      </div>
    </fetcher.Form>
  );
};
